import React, { useState, useEffect } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { format } from 'date-fns';

const CanvasJS = CanvasJSReact.CanvasJS;
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// Kolory niestandardowe
const customColorSet = [
  "#ff0000","#0000ff","#00ff00","#ffbb78","#8c564b",
  "#e377c2","#7f7f7f","#bcbd22","#17becf","#aec7e8",
  "#ff7f0e","#9467bd"
];

CanvasJS.addColorSet('customColorSet', customColorSet);

const LineChart = ({
  id, syncHandler, chartRef, chart_title, dataPoints,
  unit, min, max, initmin, initmax, miny, maxy, step
}) => {

  const customColorSetTracks = customColorSet.reduce((acc, color, index) => {
    acc[color] = index + 1;
    return acc;
  }, {});

  const [visibleStates, setVisibleStates] = useState([]);

  useEffect(() => {
    setVisibleStates(dataPoints.map(() => true));
  }, [dataPoints]);

  // Funkcja do konwersji wartości do jednostek SI
  const convertToSIUnit = (value) => {
    if (value >= 1) return `${value.toFixed(4)}  -`;
    if (value >= 1e-3) return `${(value * 1e3).toFixed(4)} m`;
    if (value >= 1e-6) return `${(value * 1e6).toFixed(4)} µ`;
    if (value >= 1e-9) return `${(value * 1e9).toFixed(4)} n`;
    if (value >= 1e-12) return `${(value * 1e12).toFixed(4)} p`;
    return `${value} `;
  };

  const transformToChartData = (inputData) => {
    const THREE_WEEKS_MAX_STEP = 252;
    const numericStep = Number(step);
    const restricted = numericStep === 1
      ? 10
      : numericStep <= THREE_WEEKS_MAX_STEP
        ? 60
        : Number.MAX_SAFE_INTEGER;
    const RESTRICTED_MINUTES = restricted * 60000;
    let seriesIndex = 0;

    const allSeries = [];

    inputData.forEach(group => {
      const { title, points } = group;
      if (!Array.isArray(points) || points.length === 0) return;

      points.forEach((serieOrList, idx) => {
        const serieArray = Array.isArray(serieOrList[0]) ? serieOrList : [serieOrList];

        serieArray.forEach(pointsList => {
          if (!Array.isArray(pointsList) || pointsList.length === 0) return;

          let lastTime = null;
          let lastPoint = null;

          const dataPoints = pointsList.map(point => {
            const newPoint = { ...point };
            const currentTime = new Date(point.x).getTime();

            if (lastTime !== null && currentTime - lastTime > RESTRICTED_MINUTES) {
              lastPoint.lineColor = "transparent";
            }

             if (
              title === "MeanSection" &&
              lastPoint &&
              lastPoint.y !== point.y
            ) {
              lastPoint.lineColor = "transparent";
            }

            lastTime = currentTime;
            lastPoint = newPoint;

            return newPoint;
          });

          allSeries.push({
            showInLegend: true,
            type: "line",
            dataPoints,
            name: `${title}${idx + 1}`,
            visible: visibleStates[seriesIndex] ?? true
          });

          seriesIndex += 1;
        });
      });
    });

    return allSeries;
  };

  const chartStyle = { height: 500, width: '90%' };

  const options = {
    colorSet: 'customColorSet',
    backgroundColor: "transparent",
    zoomEnabled: true,
    zoomType: 'xy',
    id,
    axisY: {
      tickLength: 15,
      tickColor: 'black',
      lineDashType: 'dash',
      gridDashType: 'dash',
      gridColor: 'black',
      minimum: null,
      maximum: null,
      includeZero: false,
      viewportMinimum: miny,
      viewportMaximum: maxy,
      labelFormatter: (e) => convertToSIUnit(e.value),
    },
    axisX: {
      tickColor: 'black',
      lineColor: 'black',
      lineDashType: 'dash',
      gridDashType: 'dash',
      gridThickness: 1,
      gridColor: 'black',
      minimum: initmin,
      maximum: initmax,
      viewportMinimum: min,
      viewportMaximum: max,
      valueFormatString: "DD-MM-YY HH:mm:ss.fff",
      labelFontSize: 12,
      labelMaxWidth: 50
    },
    animationEnabled: false,
    exportFileName: chart_title,
    exportEnabled: true,
    title: {
      padding: { left: 10 },
      margin: 25,
      verticalAlign: 'top',
      horizontalAlign: 'left',
      text: `${chart_title} ${unit}`,
      fontColor: 'black',
      fontSize: 30,
      border: '2px solid #fff'
    },
    legend: {
      cursor: "pointer",
      fontSize: 15,
      itemclick: function (e) {
        const index = e.dataSeriesIndex;
        if (index === undefined) return;
        setVisibleStates((prev) => {
          const newState = [...prev];
          newState[index] = !newState[index];
          setTimeout(() => chartRef.current?.render(), 0);
          return newState;
        });
      }
    },
    toolTip: {
      contentFormatter: function (e) {
        const formattedDateTime = format(e.entries[0].dataPoint.x, 'dd-MM-yy HH:mm:ss.SSS');
        const trackNumber = customColorSetTracks[e.entries[0].dataSeries.color]
        return `<strong>Track: ${trackNumber}</strong><br/>
                <strong>Date :</strong> ${formattedDateTime}<br/>
                <strong>Value:</strong> ${e.entries[0].dataPoint.y}`;
      }
    },
    data: transformToChartData(dataPoints),
    rangeChanged: syncHandler,
    toolbar: {
      itemBackgroundColor: "#d3d3d3",
      itemBackgroundColorOnHover: "#00AEEF",
      buttonBorderColor: "white",
      buttonBorderThickness: 3,
      fontColor: 'black',
      fontColorOnHover: 'black',
    }
  };

  return (
    <div className='dashboard-chart-wrapper'>
      <CanvasJSChart
        options={options}
        onRef={ref => chartRef.current = ref}
        containerProps={chartStyle}
      />
    </div>
  );
};

export default LineChart;
